import { hasExtractableTrustedDeviceInstallation } from "../../../core/account/services/extractable-trusted-device.service.ts";
import {
  detectInAppBrowser,
  isInsecurePublicPage,
} from "./browser-environment.ts";
import type { TrustedDeviceEnvironment } from "./trusted-device-fallback.ts";

/** localStorage marker: this browser signs in through the device cookie. */
export const DEVICE_COOKIE_MODE_STORAGE_KEY = "feather-trusted-device-mode";

const STORAGE_PROBE_KEY = "feather-trusted-device-probe";

type BrowserWindowLike = {
  isSecureContext?: boolean;
  crypto?: { subtle?: { generateKey?: unknown } };
  indexedDB?: { open?: unknown };
  localStorage?: Storage;
  navigator?: { cookieEnabled?: boolean; userAgent?: string };
  location?: Pick<Location, "protocol" | "hostname">;
};

function readLocalStorage(browserWindow: BrowserWindowLike) {
  try {
    return browserWindow.localStorage;
  } catch {
    // Accessing the property throws when site data is blocked.
    return undefined;
  }
}

/**
 * Whether localStorage accepts a write and returns it.
 *
 * @example
 * isLocalStorageWritable(window.localStorage); // false in old Safari private mode
 */
export function isLocalStorageWritable(storage: Storage | undefined): boolean {
  if (!storage) return false;

  try {
    storage.setItem(STORAGE_PROBE_KEY, "1");

    const readBack = storage.getItem(STORAGE_PROBE_KEY);

    storage.removeItem(STORAGE_PROBE_KEY);

    return readBack === "1";
  } catch {
    return false;
  }
}

/**
 * Remember that this browser signs in through the device cookie, so a later
 * login does not mint a key (and burn another device slot) on top of it.
 *
 * @example
 * rememberDeviceCookieMode(window.localStorage);
 */
export function rememberDeviceCookieMode(storage: Storage | undefined): void {
  try {
    storage?.setItem(DEVICE_COOKIE_MODE_STORAGE_KEY, "deviceCookie");
  } catch {
    // Best effort: without the marker the ladder simply re-evaluates.
  }
}

function prefersDeviceCookie(storage: Storage | undefined): boolean {
  try {
    return storage?.getItem(DEVICE_COOKIE_MODE_STORAGE_KEY) === "deviceCookie";
  } catch {
    return false;
  }
}

/**
 * Collect the synchronous browser facts the login ladder decides from.
 *
 * @example
 * const environment = getTrustedDeviceEnvironment(window);
 */
export function getTrustedDeviceEnvironment(
  browserWindow: BrowserWindowLike | undefined = typeof window === "undefined"
    ? undefined
    : (window as unknown as BrowserWindowLike),
): TrustedDeviceEnvironment {
  const storage = browserWindow ? readLocalStorage(browserWindow) : undefined;
  const localStorageWritable = isLocalStorageWritable(storage);

  return {
    webCrypto: typeof browserWindow?.crypto?.subtle?.generateKey === "function",
    indexedDb: typeof browserWindow?.indexedDB?.open === "function",
    localStorage: localStorageWritable,
    cookiesEnabled: browserWindow?.navigator?.cookieEnabled === true,
    insecurePublicPage: isInsecurePublicPage(browserWindow?.location),
    inAppBrowser: detectInAppBrowser(browserWindow?.navigator?.userAgent),
    hasExtractableInstallation:
      localStorageWritable && hasExtractableTrustedDeviceInstallation(storage),
    prefersDeviceCookie: localStorageWritable && prefersDeviceCookie(storage),
  };
}
